import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { components } from 'react-select';

import { Spinner } from 'app/design-system/components/Spinner';
import { Tabs } from 'app/design-system/components/Tabs';
import { useTranslation } from 'app/lang';
import {
  isChainPledgeStake,
  PledgeStakeDAO,
} from 'app/modules/BetSlip/types/backend/paymentOptions';
import { ComponentsConfig, Control, MenuList, Option } from 'app/widgets/Dropdown/Single';

import ChainEntry from '../ChainEntry';
import SingleEntry from '../SingleEntry';

import dictionary from './PledgeStakeDropdown.dictionary.json';

import styles from './PledgeStakeDropdown.module.scss';

// ---

export enum Tab {
  SINGLE = 'single',
  CHAIN = 'chain',
}

const TABS_OPTIONS = [
  {
    label: dictionary.singleLabel, id: Tab.SINGLE,
    dataGaValues: {
      ['data-ga-event']: 'cart-bidSourceTypeSelect',
      ['data-ga-event-category']: Tab.SINGLE,
    },
  },
  {
    label: dictionary.chainLabel, id: Tab.CHAIN,
    dataGaValues: {
      ['data-ga-event']: 'cart-bidSourceTypeSelect',
      ['data-ga-event-category']: Tab.CHAIN,
    },
  },
];

// ---

// eslint-disable-next-line max-params
function DropdownComponents(
  isLoading: boolean,
  activeTab: Tab,
  setActiveTab: Dispatch<SetStateAction<Tab>>,
  minValue: number,
  usedPledgeStakes: Set<number>,
  setMenuListBottom?: Dispatch<SetStateAction<number | null>>,
  menuPlacement?: string,
  menuListBottom?: number | null,
  betId?: number,
): ComponentsConfig<PledgeStakeDAO> {
  const { t } = useTranslation();
  return useMemo(() => ({
    LoadingMessage: (props) => (
      <components.LoadingMessage {...props}>
        <div className={styles.spinner}><Spinner size={16} /></div>
      </components.LoadingMessage>
    ),
    MenuList: (props) => (
      <MenuList {...props}
                setMenuListBottom={setMenuListBottom}>
        {!isLoading && (
          <Tabs<Tab>
            type="button"
            data-test="pledgeStakeDropdown-tabs"
            activeTabId={activeTab}
            tabs={TABS_OPTIONS}
            className={styles.menuHeader}
            onChange={setActiveTab} />
        )}
        {props.children}
      </MenuList>
    ),
    MenuPortal: (props) => (
      <components.MenuPortal {...props}>
        <div className={styles.portal}>
          {props.children}
        </div>
      </components.MenuPortal>
    ),
    Option: (props) => {
      const isSelected = usedPledgeStakes.has(props.data.stakeId);
      const isDisabled = props.data.maxWinning < minValue;

      return (
        <Option {...props}>
          {isChainPledgeStake(props.data) ? (
            <ChainEntry data={props.data}
                        isDisabled={isDisabled}
                        isSelected={isSelected} />
          ) : (
            <SingleEntry data={props.data}
                         betId={betId}
                         isDisabled={isDisabled}
                         isSelected={isSelected} />
          )}
        </Option>
      );
    },
    Control: (props) => (
      <Control {...props}
               customInnerProps={{ title: t(dictionary.title) }}>
        {props.children}
      </Control>
    ),
  }), [
    isLoading,
    activeTab,
    minValue,
    usedPledgeStakes,
    setMenuListBottom,
    menuPlacement,
    menuListBottom,
  ]);
}

export default DropdownComponents;
